/**
 * Hero Component
 * 
 * Project: SunMarbella Holiday Rental Website
 * Version: 1.0.0
 * Last Modified: 2025-03-09
 * 
 * Purpose:
 * Full-width hero section with background image, headline, and integrated search bar.
 * Primary above-the-fold content for homepage and key landing pages.
 * 
 * Features:
 * - Full-width background image
 * - Headline and subheadline
 * - Integrated SearchBar component
 * - Responsive background sizing
 * - Overlay for text readability
 * - Mobile-optimized layout
 * 
 * Dependencies:
 * - React 19.2.4 (JSX)
 * - SearchBar (search interface)
 * - TailwindCSS 4.1 (responsive styling)
 * 
 * Changelog:
 * - 1.0.0 (2025-03-09): Initial implementation
 */

import React, { useState, useEffect } from 'react';
import { SearchBar } from './SearchBar';
import { motion } from 'framer-motion';
import { useSearchBar } from '../contexts/SearchBarContext';
import { HERO_IMAGES, getHeroImageByTime, HeroImageType } from '../config/heroImages';

export const Hero = () => {
  const { setHeroSearchElement } = useSearchBar();
  // Set image based on browser time and update periodically
  const [currentImage, setCurrentImage] = useState<HeroImageType>(getHeroImageByTime());
  
  // Update image every minute to catch time changes
  useEffect(() => {
    const updateImage = () => {
      const newImage = getHeroImageByTime();
      if (newImage !== currentImage) {
        setCurrentImage(newImage);
      }
    };
    
    // Check immediately
    updateImage();
    
    // Then check every minute
    const interval = setInterval(updateImage, 60000);
    
    return () => clearInterval(interval);
  }, [currentImage]);
  
  return (
    <section className="relative h-[90vh] min-h-[500px] md:min-h-[600px] flex flex-col justify-end md:justify-center items-center pt-20 pb-6 md:pb-0">
      {/* Background Video/Image */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <img 
          src={HERO_IMAGES[currentImage].src}
          alt={HERO_IMAGES[currentImage].alt}
          className="w-full h-full object-cover"
          loading="eager"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-black/30 backdrop-blur-[1px]"></div>
        {/* Bottom gradient for search bar visibility */}
        <div className="absolute bottom-0 left-0 right-0 h-[220px] bg-gradient-to-t from-black/35 to-transparent pointer-events-none"></div>
      </div>

      {/* Content container */}
      <div className="relative z-10 w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="flex flex-col items-center space-y-4 md:space-y-6 lg:space-y-8"
        >
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white tracking-tighter leading-tight uppercase">
            <span className="md:hidden">MARBELLA <span className="text-brand-sun">FAMILY</span> RENTALS DONE RIGHT</span>
            <span className="hidden md:block">MARBELLA <span className="text-brand-sun">FAMILY</span><br />RENTALS DONE RIGHT</span>
          </h1>
          
          <p className="hidden lg:block text-xl md:text-2xl text-white/90 font-medium max-w-3xl mx-auto tracking-tight">
            Inspected homes. Better direct rates. Real local support from first enquiry to checkout.
          </p>

          <p className="md:hidden text-sm text-white/90 font-semibold max-w-xs mx-auto leading-relaxed">
            Inspected homes, direct rates, real local support.
          </p>
        </motion.div>

          {/* Search Bar - Visible on all devices */}
        <motion.div
          ref={setHeroSearchElement}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="pt-4 md:pt-6 lg:pt-10 w-full"
        >
          <div className="shadow-[0_8px_32px_rgba(0,0,0,0.20)] rounded-3xl">
            <SearchBar />
          </div>
        </motion.div>
      </div>
    </section>
  );
};
